import { useState, useEffect } from 'react';
import {
  Button,
  TextField,
  Typography,
  Tooltip,
  Modal,
  Paper,
  Box,
  RadioGroup,
  FormControlLabel,
  Radio,
} from '@mui/material';
import { Search, X, Info, AlertTriangle } from 'lucide-react';
import { toPattern } from 'vanilla-masker';
import { cnpj, cpf } from 'cpf-cnpj-validator';
import { AnimatePresence, motion } from 'framer-motion';
import {
  ModalContainer,
  ModalHeader,
  ModalTitleContainer,
  ModalTitle,
  ModalDescription,
  ModalContent,
  ModalFooter,
  StepperContainer,
  StepperWrapper,
  StepNumber,
  StepLabel,
  StepItem,
  StepDivider,
  ProgressBar,
  ProgressFill,
  TabsContainer,
  TabsList,
  TabButton,
  SearchContainer,
  SectionHeading,
  StickyHeader,
  StepContent,
  CloseButton,
  SourcesStepContainer,
  SearchIconWrapper,
  StyledWarning,
} from './styles';
import { useExternalFont } from 'contexts/externalFontContext';
import { useAuth, useCommercialPlan, useModel } from 'contexts';

import { FontAccessLevel, SearchType } from 'types/enums';
import {
  IAvailableExternalFontViewModel,
  IModelDossierViewModel,
} from 'viewModels';
import ComplementaryDataForm from 'components/ComplementaryDataForm';
import { IRequestDossierInputModel } from 'inputModels';

import { MASK_CPF, MASK_CNPJ } from '../../../constants';
import FontList from 'components/FontList';
import ModelList from 'components/ModelList';

interface DossierRequestModalProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: IRequestDossierInputModel) => void;
}

const containerVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.2 } },
  exit: { opacity: 0, y: 20, transition: { duration: 0.2 } },
};

const stepVariants = {
  enter: (direction: number) => ({
    x: direction > 0 ? 20 : -20,
    opacity: 0,
  }),
  center: {
    x: 0,
    opacity: 1,
    transition: { duration: 0.2 },
  },
  exit: (direction: number) => ({
    x: direction < 0 ? 20 : -20,
    opacity: 0,
    transition: { duration: 0.2 },
  }),
};

const NewDossierModal = ({
  open,
  onClose,
  onSubmit,
}: DossierRequestModalProps) => {
  const { externalFonts } = useExternalFont();
  const { models, handleCreateModel } = useModel();
  const { user } = useAuth();
  const { userCommercialPlan } = useCommercialPlan();

  const [step, setStep] = useState(1);
  const [direction, setDirection] = useState(0);
  const [documentType, setDocumentType] = useState<'cpf' | 'cnpj'>('cpf');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedModel, setSelectedModel] = useState<IModelDossierViewModel>();
  const [requiredParams, setRequiredParams] = useState<string[]>([]);
  const [isValidDocument, setIsValidDocument] = useState(false);
  const [availableFonts, setAvailableFonts] = useState<
    IAvailableExternalFontViewModel[]
  >([]);
  const [sourceTab, setSourceTab] = useState<'fonts' | 'models'>('fonts');
  const [isComplementaryFormValid, setIsComplementaryFormValid] =
    useState(true);
  const [complementaryFormData, setComplementaryFormData] = useState<
    Partial<IRequestDossierInputModel>
  >({});
  const [document, setDocument] = useState('');
  const [description, setDescription] = useState('');
  const [selectedExternalFonts, setSelectedExternalFonts] = useState<string[]>(
    []
  );
  const [selectedExtraFonts, setSelectedExtraFonts] = useState<string[]>([]);
  const [showFontLimitWarning, setShowFontLimitWarning] = useState(false);
  const [generateModelOption, setGenerateModelOption] = useState<'yes' | 'no'>(
    'no'
  );
  const [modelName, setModelName] = useState('');
  const [modelNameError, setModelNameError] = useState<string | null>(null);

  // Limite de PUBLIC do plano
  const maxPublicFonts = userCommercialPlan?.maxPublicFonts ?? 20;

  useEffect(() => {
    if (document.length > 14) {
      setDocumentType('cnpj');
      setAvailableFonts(
        externalFonts.filter(
          font => font.type === SearchType.CNPJ || font.type === SearchType.BOTH
        )
      );
    } else {
      setDocumentType('cpf');
      setAvailableFonts(
        externalFonts.filter(
          font => font.type === SearchType.CPF || font.type === SearchType.BOTH
        )
      );
    }
  }, [document, externalFonts]);

  useEffect(() => {
    const cleanDocument = document.replace(/\D/g, '');
    const minLength = documentType === 'cnpj' ? 14 : 11;

    if (!cleanDocument) {
      setIsValidDocument(false);
    } else if (cleanDocument.length >= minLength) {
      setIsValidDocument(
        documentType === 'cnpj'
          ? cnpj.isValid(cleanDocument)
          : cpf.isValid(cleanDocument)
      );
      setSelectedExternalFonts([]);
    } else {
      setIsValidDocument(false);
    }
  }, [document, documentType]);

  // Efeito central: params obrigatórios (todas as fontes),
  // extras (apenas PUBLIC acima do limite) e aviso.
  useEffect(() => {
    // requiredParams com TODAS as fontes selecionadas
    if (selectedExternalFonts.length > 0) {
      const params = new Set<string>();
      selectedExternalFonts.forEach(sourceKey => {
        const font = availableFonts.find(f => f.key === sourceKey);
        if (font?.requiredParameters) {
          font.requiredParameters.forEach(param => params.add(param));
        }
      });
      setRequiredParams(Array.from(params));
    } else {
      setRequiredParams([]);
    }

    // Mantém a ordem de seleção
    // Considera apenas as PUBLIC para o limite
    const selectedPublicKeys = selectedExternalFonts.filter(key => {
      const font = availableFonts.find(f => f.key === key);
      return font?.accessLevel === FontAccessLevel.PUBLIC;
    });

    // As extras são as PUBLIC acima do limite
    const publicExtras =
      maxPublicFonts > 0
        ? selectedPublicKeys.slice(maxPublicFonts)
        : [...selectedPublicKeys];

    setSelectedExtraFonts(publicExtras);

    // Mostra aviso apenas se houver PUBLIC extras
    setShowFontLimitWarning(publicExtras.length > 0);
  }, [selectedExternalFonts, availableFonts, maxPublicFonts]);

  const filteredFonts = availableFonts
    .filter(font => !font.disabled) // 👈 só mostra fontes habilitadas
    .filter(font => font.name.toLowerCase().includes(searchTerm.toLowerCase()))
    .sort((a, b) => a.name.localeCompare(b.name));

  const filteredModels = models
    ?.filter(
      model =>
        model.name.toLowerCase().includes(searchTerm.toLowerCase()) &&
        (model.type ===
          (documentType === 'cpf' ? SearchType.CPF : SearchType.CNPJ) ||
          model.type === SearchType.BOTH)
    )
    .sort((a, b) => {
      if (a.disabled && !b.disabled) return 1;
      if (!a.disabled && b.disabled) return -1;
      return a.name.localeCompare(b.name);
    });

  const handleSourceToggle = (sourceKey: string) => {
    setSelectedExternalFonts(prev =>
      prev.includes(sourceKey)
        ? prev.filter(key => key !== sourceKey)
        : [...prev, sourceKey]
    );
  };

  const handleModelSelect = (model: IModelDossierViewModel) => {
    if (model.disabled) return;
    if (selectedModel?.id === model.id) {
      setSelectedModel(undefined);
      setSelectedExternalFonts([]);
      return;
    }
    setSelectedModel(model);
    const modelFontKeys = model.fonts
      .filter(font => !font.disabled)
      .map(font => font.key);

    setSelectedExternalFonts(modelFontKeys);
  };

  const nextStep = async () => {
    if (step === 1 && (!document || !isValidDocument)) return;
    if (step === 2 && selectedExternalFonts.length === 0) return;
    if (step === 3 && generateModelOption === 'yes') {
      if (!modelName.trim()) {
        setModelNameError('O nome do modelo é obrigatório.');
        return;
      }
      if (models?.some(item => item.name === modelName)) {
        setModelNameError(
          'Um modelo com este nome já existe. Escolha outro nome.'
        );
        return;
      }
      const modelCreated = await handleCreateModel(
        modelName,
        selectedExternalFonts
      );
      if (!modelCreated) return;
      setGenerateModelOption('no');
      setModelName('');
      setModelNameError(null);
    }
    if (step < 4) {
      setDirection(1);
      setStep(step + 1);
    }
  };

  const prevStep = () => {
    if (step > 1) {
      setDirection(-1);
      setStep(step - 1);
    }
  };

  const handleSubmit = () => {
    if (!isValidDocument || !document) return;

    const mergedData = {
      document: document.replace(/\D/g, ''),
      description,
      selectedExternalFonts,
      selectedExtraFonts,
      ...complementaryFormData,
    };

    const cleanedData = Object.fromEntries(
      Object.entries(mergedData).filter(
        ([_, value]) =>
          value !== undefined &&
          value !== '' &&
          !(Array.isArray(value) && value.length === 0)
      )
    ) as unknown as IRequestDossierInputModel;

    if (!cleanedData.document) return;

    if (cleanedData.identityNumber) {
      cleanedData.identityNumber = cleanedData.identityNumber.replace(
        /\D/g,
        ''
      );
    }
    if (cleanedData.cep) {
      cleanedData.cep = cleanedData.cep.replace(/\D/g, '');
    }

    onSubmit(cleanedData);

    setDocument('');
    setDescription('');
    setSelectedExternalFonts([]);
    setSelectedExtraFonts([]);
    setComplementaryFormData({});
    setStep(1);
    setSourceTab('fonts');
    setIsComplementaryFormValid(true);
    setSelectedModel(undefined);
    setSearchTerm('');
    setGenerateModelOption('no');
    setModelName('');
    setModelNameError(null);
    onClose();
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      aria-labelledby="modal-title"
      aria-describedby="modal-description"
      sx={{ display: 'flex', flexDirection: 'column' }}
    >
      <Paper
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          bgcolor: 'background.paper',
          borderRadius: 2,
          outline: 'none',
          width: { xs: '90vw', sm: '80vw', md: '60vw' },
          minHeight: { xs: '70vh', sm: '80vh' },
          maxWidth: '100%',
        }}
      >
        <AnimatePresence>
          <motion.div
            initial="hidden"
            animate="visible"
            exit="exit"
            variants={containerVariants}
          >
            <ModalContainer>
              <ModalHeader>
                <ModalTitleContainer>
                  <ModalTitle id="modal-title">Novo Dossiê</ModalTitle>
                  <ModalDescription id="modal-description">
                    Complete as etapas para gerar um novo dossiê
                  </ModalDescription>
                </ModalTitleContainer>
                <CloseButton onClick={onClose}>
                  <X size={20} />
                </CloseButton>
              </ModalHeader>
              <ModalContent>
                <StepperContainer>
                  <StepperWrapper>
                    <StepItem>
                      <StepNumber active={step >= 1}>1</StepNumber>
                      <StepLabel>Documento</StepLabel>
                    </StepItem>
                    <StepDivider />
                    <StepItem>
                      <StepNumber active={step >= 2}>2</StepNumber>
                      <StepLabel>Fontes</StepLabel>
                    </StepItem>
                    <StepDivider />
                    <StepItem>
                      <StepNumber active={step >= 3}>3</StepNumber>
                      <StepLabel>Criar Modelo</StepLabel>
                    </StepItem>
                    <StepDivider />
                    <StepItem>
                      <StepNumber active={step >= 4}>4</StepNumber>
                      <StepLabel>Dados Complementares</StepLabel>
                    </StepItem>
                  </StepperWrapper>
                  <ProgressBar>
                    <ProgressFill progress={(step / 4) * 100} />
                  </ProgressBar>
                </StepperContainer>

                <div style={{ position: 'relative', width: '100%' }}>
                  <AnimatePresence custom={direction} mode="wait">
                    {step === 1 && (
                      <StepContent
                        key="step1"
                        custom={direction}
                        variants={stepVariants}
                        initial="enter"
                        animate="center"
                        exit="exit"
                        style={{
                          position: 'absolute',
                          width: '100%',
                        }}
                      >
                        <SectionHeading>
                          Informe o documento para consulta
                        </SectionHeading>
                        <TabsContainer>
                          <TabsList>
                            <TabButton
                              active={documentType === 'cpf'}
                              onClick={() => setDocumentType('cpf')}
                            >
                              CPF
                            </TabButton>
                            <TabButton
                              active={documentType === 'cnpj'}
                              onClick={() => setDocumentType('cnpj')}
                            >
                              CNPJ
                            </TabButton>
                          </TabsList>
                        </TabsContainer>
                        <TextField
                          name="document"
                          fullWidth
                          size="small"
                          label={
                            documentType === 'cpf'
                              ? 'Informe o CPF'
                              : 'Informe o CNPJ'
                          }
                          placeholder={
                            documentType === 'cpf'
                              ? '000.000.000-00'
                              : '00.000.000/0000-00'
                          }
                          value={
                            documentType === 'cpf'
                              ? toPattern(document || '', MASK_CPF)
                              : toPattern(document || '', MASK_CNPJ)
                          }
                          onChange={e => setDocument(e.target.value)}
                          slotProps={{
                            htmlInput: {
                              maxLength: 18,
                            },
                          }}
                          error={
                            document
                              ? document.replace(/\D/g, '').length >=
                                (documentType === 'cnpj' ? 14 : 11)
                                ? !isValidDocument
                                : false
                              : false
                          }
                          helperText={
                            document
                              ? document.replace(/\D/g, '').length <
                                (documentType === 'cnpj' ? 14 : 11)
                                ? 'Documento incompleto'
                                : !isValidDocument
                                ? 'Documento inválido'
                                : undefined
                              : undefined
                          }
                          sx={{ marginBottom: 2 }}
                        />
                        <TextField
                          fullWidth
                          size="small"
                          label="Descrição do Dossiê (Opcional)"
                          placeholder="Adicione uma breve descrição para este dossiê"
                          multiline
                          rows={3}
                          value={description}
                          onChange={e => setDescription(e.target.value)}
                          sx={{ marginBottom: 2 }}
                        />
                        <Box
                          sx={{
                            mt: 2,
                            p: 2,
                            backgroundColor: 'grey.100',
                            borderRadius: 1,
                            border: '1px solid',
                            borderColor: 'grey.300',
                            display: 'flex',
                            alignItems: 'flex-start',
                            gap: 1.5,
                          }}
                        >
                          <Info
                            size={28}
                            color="#293778"
                            style={{ marginTop: '2px', flexShrink: 0 }}
                          />
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ lineHeight: 2 }}
                          >
                            <strong>Dica:</strong> Preencha o CPF ou CNPJ que
                            deseja investigar. A descrição do dossiê é opcional,
                            mas pode ajudá-lo(a) a organizar e identificar suas
                            consultas futuras.
                            <br />
                            <em>
                              Ex: “Análise de crédito - Empresa Z“ ou
                              “Verificação de antecedentes - Candidato X“.
                            </em>
                          </Typography>
                        </Box>
                      </StepContent>
                    )}

                    {step === 2 && (
                      <StepContent
                        key="step2"
                        custom={direction}
                        variants={stepVariants}
                        initial="enter"
                        animate="center"
                        exit="exit"
                        style={{ position: 'absolute', width: '100%' }}
                      >
                        <SourcesStepContainer>
                          <StickyHeader>
                            <SectionHeading>
                              Selecione as fontes ou modelos para consulta
                            </SectionHeading>
                            <TabsContainer>
                              <TabsList>
                                <TabButton
                                  active={sourceTab === 'fonts'}
                                  onClick={() => setSourceTab('fonts')}
                                >
                                  Fontes
                                </TabButton>
                                <TabButton
                                  active={sourceTab === 'models'}
                                  onClick={() => setSourceTab('models')}
                                >
                                  Modelos
                                </TabButton>
                              </TabsList>
                              <SearchContainer>
                                <SearchIconWrapper>
                                  <Search size={16} />
                                </SearchIconWrapper>
                                <TextField
                                  size="small"
                                  placeholder={
                                    sourceTab === 'fonts'
                                      ? 'Pesquisar Fontes'
                                      : 'Pesquisar Modelos'
                                  }
                                  value={searchTerm}
                                  onChange={e => setSearchTerm(e.target.value)}
                                  sx={{
                                    width: '350px',
                                    '& .MuiOutlinedInput-root': {
                                      paddingLeft: '32px',
                                    },
                                  }}
                                />
                              </SearchContainer>
                            </TabsContainer>
                          </StickyHeader>
                          {sourceTab === 'fonts' ? (
                            <FontList
                              fonts={filteredFonts}
                              selectedFonts={selectedExternalFonts}
                              onFontToggle={handleSourceToggle}
                              searchTerm={searchTerm}
                              extraFonts={selectedExtraFonts}
                            />
                          ) : (
                            <ModelList
                              models={filteredModels || []}
                              selectedModelId={selectedModel?.id}
                              onModelSelect={handleModelSelect}
                              maxFonts={maxPublicFonts}
                              searchTerm={searchTerm}
                            />
                          )}
                        </SourcesStepContainer>
                      </StepContent>
                    )}

                    {step === 3 && (
                      <StepContent
                        key="step3"
                        custom={direction}
                        variants={stepVariants}
                        initial="enter"
                        animate="center"
                        exit="exit"
                        style={{ position: 'absolute', width: '100%' }}
                      >
                        <SectionHeading>
                          Deseja gerar um modelo com as fontes selecionadas?
                        </SectionHeading>
                        <div
                          style={{
                            marginLeft: '16px',
                            display: 'flex',
                            width: '100%',
                            alignItems: 'center',
                          }}
                        >
                          <RadioGroup
                            value={generateModelOption}
                            onChange={e =>
                              setGenerateModelOption(
                                e.target.value as 'yes' | 'no'
                              )
                            }
                            row
                          >
                            <FormControlLabel
                              value="yes"
                              control={<Radio />}
                              label="Sim"
                            />
                            <FormControlLabel
                              value="no"
                              control={<Radio />}
                              label="Não"
                            />
                          </RadioGroup>
                        </div>

                        {generateModelOption === 'yes' && (
                          <>
                            <TextField
                              size="small"
                              label="Nome do Modelo"
                              variant="outlined"
                              fullWidth
                              value={modelName}
                              onChange={e => {
                                setModelName(e.target.value);
                                setModelNameError(null);
                              }}
                              error={!!modelNameError}
                              helperText={modelNameError}
                              sx={{ mt: 2 }}
                            />
                          </>
                        )}
                        <Typography variant="subtitle1" sx={{ mt: 2, mb: 1 }}>
                          Fontes selecionadas:
                        </Typography>
                        <FontList
                          fonts={availableFonts.filter(
                            font =>
                              selectedExternalFonts.includes(font.key) &&
                              font.name.toLowerCase()
                          )}
                          selectedFonts={selectedExternalFonts}
                          extraFonts={selectedExtraFonts}
                        />
                      </StepContent>
                    )}

                    {step === 4 && (
                      <StepContent
                        key="step4"
                        custom={direction}
                        variants={stepVariants}
                        initial="enter"
                        animate="center"
                        exit="exit"
                        style={{ position: 'absolute', width: '100%' }}
                      >
                        <ComplementaryDataForm
                          requiredParams={requiredParams}
                          onValidChange={setIsComplementaryFormValid}
                          onDataChange={setComplementaryFormData}
                        />
                      </StepContent>
                    )}
                  </AnimatePresence>
                </div>
              </ModalContent>
              <ModalFooter>
                {step > 1 ? (
                  <Button
                    variant="outlined"
                    onClick={prevStep}
                    sx={{ color: '#293778', borderColor: '#293778' }}
                  >
                    Voltar
                  </Button>
                ) : (
                  <div></div>
                )}

                {showFontLimitWarning && (
                  <StyledWarning>
                    <AlertTriangle size={14} />
                    {`Selecionar mais que ${maxPublicFonts} fontes PÚBLICAS gera custo adicional na geração de dossiês.`}
                  </StyledWarning>
                )}

                {step < 4 ? (
                  <Button
                    variant="contained"
                    onClick={nextStep}
                    disabled={
                      (step === 1 && (!document || !isValidDocument)) ||
                      (step === 2 && selectedExternalFonts.length === 0) ||
                      (step === 3 &&
                        generateModelOption === 'yes' &&
                        !modelName.trim())
                    }
                    sx={{
                      backgroundColor: '#293778',
                      '&:hover': { backgroundColor: '#1e2654' },
                    }}
                  >
                    Próximo
                  </Button>
                ) : (
                  <Tooltip
                    title={
                      !isComplementaryFormValid
                        ? 'Preencha todos os campos obrigatórios'
                        : ''
                    }
                  >
                    <span>
                      <Button
                        variant="contained"
                        onClick={handleSubmit}
                        disabled={!isComplementaryFormValid || !isValidDocument}
                        sx={{
                          backgroundColor: '#293778',
                          '&:hover': { backgroundColor: '#1e2654' },
                        }}
                      >
                        Gerar Dossiê
                      </Button>
                    </span>
                  </Tooltip>
                )}
              </ModalFooter>
            </ModalContainer>
          </motion.div>
        </AnimatePresence>
      </Paper>
    </Modal>
  );
};

export default NewDossierModal;
